import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import ButtonComponent from "../ButtonComponent/Button";
import "./ForgotPassword.css";
import apiService from "../../service";
import ToastMessage from "../Common/ReactToastifyNotification/Toastify";
// MainLogo removed - not used in this component
import DrawerLogo from "../../assets/DrawerLogo.png";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Loader from "../Common/Loader";
import eye from "../../assets/eye.svg";
import viewOff from "../../assets/view-off.svg";
import PasswordStrengthBar from "../Common/PasswordStrength/PasswordStrength";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inputsRef = useRef([]);
  const [resetPasswordId, setResetPasswordId] = useState(null);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    setEmailError(validateEmail(e.target.value) ? "" : "Invalid email");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if email is provided
    if (!email) {
      setEmailError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Invalid email");
      return;
    }

    setEmailError("");
    setOpen(true);

    const forgotPasswordPayLoad = {
      email: email,
    };

    try {
      const apiForgotRes = await apiService.forgotpassword(
        forgotPasswordPayLoad
      );

      if (apiForgotRes.status === "true") {
        // server returns a changePasswordId (or similar) to identify the flow
        const resetId =
          apiForgotRes.data?.changePasswordId ||
          apiForgotRes.data?.verificationId ||
          apiForgotRes.data?.id ||
          null;
        setResetPasswordId(resetId);
        setOpen(false);
        // ToastMessage.success(apiForgotRes.message || "OTP sent to your email");
        // hide email input and show OTP inputs
        setShowOtp(true);
      } else {
        setEmailError(apiForgotRes.message);
      }
    } catch (error) {
      console.log(error);
      setEmailError("An error occurred. Please try again.");
      setOpen(false);
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (showOtp && inputsRef.current && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [showOtp]);

  const handleDigitChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 1);
    setOtpDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
    if (val) {
      const nextEl = inputsRef.current[idx + 1];
      if (nextEl) nextEl.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otpDigits[idx]) {
        setOtpDigits((prev) => {
          const next = [...prev];
          next[idx] = "";
          return next;
        });
      } else {
        const prevEl = inputsRef.current[idx - 1];
        if (prevEl) {
          prevEl.focus();
          setOtpDigits((prev) => {
            const next = [...prev];
            next[idx - 1] = "";
            return next;
          });
        }
      }
    } else if (e.key === "ArrowLeft") {
      const prevEl = inputsRef.current[idx - 1];
      if (prevEl) prevEl.focus();
    } else if (e.key === "ArrowRight") {
      const nextEl = inputsRef.current[idx + 1];
      if (nextEl) nextEl.focus();
    }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const digits = paste
      .replace(/\D/g, "")
      .slice(0, 6 - idx)
      .split("");
    if (digits.length === 0) return;
    setOtpDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < digits.length; i++) {
        next[idx + i] = digits[i];
        if (inputsRef.current[idx + i])
          inputsRef.current[idx + i].value = digits[i];
      }
      return next;
    });
    const focusIndex = Math.min(5, idx + digits.length);
    const focusEl = inputsRef.current[focusIndex];
    if (focusEl) focusEl.focus();
    // Do not auto-submit after paste; wait for user to press Verify
  };

  const validatePasswordField = (name, value) => {
    const errors = { ...formErrors };
    const passwordRegex =
      /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,15}$/;

    if (name === "password") {
      if (!value) {
        errors.password = "Password is required";
      } else if (value.length < 8 || value.length > 15) {
        errors.password = "Password must be 8 to 15 characters";
      } else if (!passwordRegex.test(value)) {
        errors.password =
          "Password must contain at least one number and one special character";
      } else {
        errors.password = "";
      }
    }

    if (name === "confirmPassword") {
      if (!value) {
        errors.confirmPassword = "Please confirm your password";
      } else if (value !== newPassword) {
        errors.confirmPassword = "Passwords do not match";
      } else {
        errors.confirmPassword = "";
      }
    }

    setFormErrors(errors);
    return errors;
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    if (name === "password") setNewPassword(value);
    if (name === "confirmPassword") setConfirmPassword(value);
    validatePasswordField(name, value);
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword((s) => !s);

  const handleChangePassword = async () => {
    // validate both fields
    const e1 = validatePasswordField("password", newPassword);
    const e2 = validatePasswordField("confirmPassword", confirmPassword);
    if (e1.password || e2.confirmPassword) return;

    // ensure otp provided
    const otp = otpDigits.join("");
    if (!/^[0-9]{6}$/.test(otp) && !resetPasswordId) {
      setOtpError("Please enter the 6-digit OTP");
      return;
    }

    setVerifying(true);
    try {
      // Build payload expected by resetPasswordFun
      const payload = { new_password: newPassword, forgot_password_otp: otp };

      const resp = await apiService.resetPasswordFun(payload);
      // resetPasswordFun returns { status_code: 200 } in ResetPassword.js
      if (resp && (resp.status_code === 200 || resp.status === "true")) {
        ToastMessage.success(resp.message || "Password changed successfully");
        navigate("/login");
      } else {
        const msg = resp?.message || "Failed to change password";
        ToastMessage.error(msg);
      }
    } catch (err) {
      const msg =
        (err && (err.message || err?.data?.message)) ||
        "Failed to change password";
      ToastMessage.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  // Note: OTP verification flow is handled implicitly when changing password (resetPasswordFun).

  return (
    <div className="main-container">
      {open && <Loader open={open} handleClose={() => setOpen(false)} />}
      <Box className="imagecontainer">
        {/* <img src={login} alt="Background" /> */}
        <div className="bg-logo"></div>
      </Box>
      <Box className="Rightcontainer">
        <Box sx={{ maxWidth: 350, width: "100%" }}>
          <Box className="righttopconatiner">
            <div>
              <img src={DrawerLogo} alt="Logo" className="Toplogo" />
            </div>
            <Typography
              className="Backtologin"
              onClick={() => navigate("/login")}
            >
              <ArrowBackIosNewIcon
                className="arrowicon"
                onClick={() => navigate("/login")}
              />{" "}
              Back to Sign In
            </Typography>
            <Typography className="forgot">Forgot Your Password?</Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            {!showOtp ? (
              <>
                <Typography variant="Subtitle-1">
                  Enter your email address and we’ll send you a link to reset
                  your password.
                </Typography>
                <TextField
                  label="Email Address*"
                  variant="outlined"
                  className="inputfield"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleChange}
                  error={Boolean(emailError)}
                  helperText={emailError}
                  fullWidth
                />
                <ButtonComponent
                  className="button"
                  type="submit"
                  text="continue"
                />
              </>
            ) : (
              <div className="otp-section">
                <p className="otp-instructions">
                  Enter the 6-digit OTP sent to your email
                </p>
                <div className="otp-input-row">
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputsRef.current[idx] = el)}
                      className="otp-digit"
                      value={digit}
                      onChange={(e) => handleDigitChange(e, idx)}
                      onKeyDown={(e) => handleKeyDown(e, idx)}
                      onPaste={(e) => handlePaste(e, idx)}
                      maxLength={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      style={{ textAlign: "center" }}
                    />
                  ))}
                </div>
                {otpError && <Typography color="error">{otpError}</Typography>}

                {/* Password fields shown below OTP */}
                <TextField
                  label="New Password*"
                  variant="outlined"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  error={Boolean(formErrors.password)}
                  helperText={formErrors.password}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? (
                            <img
                              src={viewOff}
                              alt="viewOff"
                              className="eye-icon"
                            />
                          ) : (
                            <img
                              src={eye}
                              alt="eye-icon"
                              className="eye-icon"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm New Password*"
                  variant="outlined"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handlePasswordChange}
                  error={Boolean(formErrors.confirmPassword)}
                  helperText={formErrors.confirmPassword}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <img
                              src={viewOff}
                              alt="viewOff"
                              className="eye-icon"
                            />
                          ) : (
                            <img
                              src={eye}
                              alt="eye-icon"
                              className="eye-icon"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box className="passwordStrength">
                  <PasswordStrengthBar password={newPassword} />
                </Box>

                <ButtonComponent
                  className="button"
                  type="button"
                  text={verifying ? "Resetting..." : "Reset Password"}
                  onClick={() => handleChangePassword()}
                  disabled={
                    verifying ||
                    !newPassword ||
                    !confirmPassword ||
                    !!formErrors.password ||
                    !!formErrors.confirmPassword ||
                    (otpDigits.some((d) => !d) && !resetPasswordId)
                  }
                />
              </div>
            )}
          </form>
        </Box>
      </Box>
    </div>
  );
};

export default ForgotPassword;
